import { create } from 'zustand';

import type { AudioRecorder } from '@/shared/lib/audio-recorder';

import { calculateRecordingLength } from '../lib/recording-utils';
import type { RecorderStatus, RecordingLength } from './audio-recording.types';

type RecordingState = {
    recordId?: string;
    clientId?: string;
    startedAt?: string;
    audioRecorder?: AudioRecorder;
    status: RecorderStatus;
    hasStartFailed: boolean;
    shortLengthThreshold?: number;
    longLengthThreshold?: number;
    length: RecordingLength;
    isSilent: boolean;
    setIsSilent: (isSilent: boolean) => void;
    getElapsedTime: () => number;
    setElapsedTime: (elapsedTime: number) => void;

    _initialize: (
        clientId: string,
        shortLengthThreshold?: number,
        longLengthThreshold?: number,
    ) => void;
    _start: (recordId: string, audioRecorder: AudioRecorder) => boolean;
    _pause: () => boolean;
    _resume: () => boolean;
    _stop: () => boolean;
    _reset: () => void;
    _failStart: () => void;
    _clearStartFailure: () => void;
};

let elapsedTime = 0;

export const useRecordingStore = create<RecordingState>((set, get) => ({
    clientId: undefined,
    recordId: undefined,
    status: 'idle',
    hasStartFailed: false,
    length: 'normal',
    startedAt: undefined,
    audioRecorder: undefined,
    isSilent: false,
    setIsSilent: (isSilent: boolean) => set({ isSilent }),
    setElapsedTime: (newElapsedTime: number) => {
        const { status, shortLengthThreshold, longLengthThreshold } = get();

        if (status !== 'recording') {
            return;
        }

        const length = calculateRecordingLength(
            newElapsedTime,
            shortLengthThreshold,
            longLengthThreshold,
        );
        set({ length });

        elapsedTime = newElapsedTime;
    },
    getElapsedTime: () => elapsedTime,
    _initialize: (
        clientId: string,
        shortLengthThreshold?: number,
        longLengthThreshold?: number,
    ) => {
        set({
            status: 'idle',
            hasStartFailed: false,
            clientId,
            shortLengthThreshold,
            longLengthThreshold,
            length: shortLengthThreshold ? 'too-short' : 'normal',
        });
    },
    _start: (recordId: string, audioRecorder: AudioRecorder) => {
        const { status } = get();
        if (status !== 'idle') {
            return false;
        }

        elapsedTime = 0;

        set({
            status: 'recording',
            hasStartFailed: false,
            startedAt: new Date().toISOString(),
            audioRecorder,
            recordId,
        });

        return true;
    },
    _pause: () => {
        const { status } = get();
        if (status !== 'recording') {
            return false;
        }

        set({ status: 'paused' });
        return true;
    },
    _resume: () => {
        const { status } = get();
        if (status !== 'paused' && status !== 'stopped') {
            return false;
        }

        set({ status: 'recording' });
        return true;
    },
    _stop: () => {
        const { status } = get();
        if (status !== 'recording' && status !== 'paused') {
            return false;
        }

        set({ status: 'stopped' });
        return true;
    },
    _reset: () => {
        elapsedTime = 0;

        const { audioRecorder, shortLengthThreshold } = get();

        audioRecorder?.dispose();

        set({
            status: 'idle',
            length: shortLengthThreshold ? 'too-short' : 'normal',
            recordId: undefined,
            startedAt: undefined,
            audioRecorder: undefined,
        });
    },
    // Deliberately outside _reset(): the failure must outlive the idle remount _reset() causes.
    _failStart: () => {
        get()._reset();
        set({ hasStartFailed: true });
    },
    _clearStartFailure: () => set({ hasStartFailed: false }),
}));
